import { useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, History, Loader2 } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Tooltip } from "@/components/atoms/Tooltip";
import { cn } from "@/lib/cn";
import type { Campaign, CampaignRun } from "@/types/campaign";
import { IN_FLIGHT, type CampaignRunsState, type Outcome } from "./campaignRuns";
import { RUN_STATUS_TONE, displayRunStatus } from "./campaignConstants";

/** Whether `Intl` can resolve the campaign's zone — it also accepts "IST". */
function resolvableZone(timeZone: string | null | undefined): string | undefined {
  if (!timeZone) return undefined;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return timeZone;
  } catch {
    return undefined;
  }
}

/**
 * A run's instant split into the day it fell on and the time within it, read in
 * the campaign's own timezone — the frame the schedule was written in, so a
 * 09:00 daily campaign reads as 09:00 here whoever is looking.
 */
function zonedParts(iso: string | null | undefined, timeZone: string | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const opts: Intl.DateTimeFormatOptions = timeZone ? { timeZone } : {};
  return {
    date: d,
    day: d.toLocaleDateString("en-GB", { ...opts, day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-GB", { ...opts, hour: "2-digit", minute: "2-digit", hour12: false }),
    seconds: d.toLocaleTimeString("en-GB", {
      ...opts,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }),
  };
}

/** "1.8s" / "340ms" / "2m 05s" — how long the dispatch itself took. */
function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null || ms < 0) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

/** "in 3 hours" / "2 days ago" for a scheduled or past instant. */
function relativeTo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMinutes = Math.round((then - Date.now()) / 60_000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["minute", 60],
    ["hour", 24],
    ["day", 7],
    ["week", 4.35],
    ["month", 12],
  ];
  const rtf = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
  let value = diffMinutes;
  for (const [unit, per] of units) {
    if (Math.abs(value) < per) return rtf.format(Math.round(value), unit);
    value /= per;
  }
  return rtf.format(Math.round(value), "year");
}

/**
 * One number and its label, doubling as a filter for the list below — the
 * quickest path from "3 failed" to *which* three is clicking the number.
 */
function RunStat({
  label,
  value,
  tone = "neutral",
  hint,
  selected,
  onClick,
}: {
  label: string;
  value: number;
  tone?: "neutral" | "success" | "warning" | "danger";
  hint: string;
  selected?: boolean;
  onClick?: () => void;
}) {
  // A zero stays muted whatever its tone — only a failure that actually
  // happened should read as red at a glance.
  const active = value > 0;
  const toneClass = {
    neutral: "border-bridge-border bg-bridge-bg/60 text-bridge-fg",
    success: "border-bridge-success/30 bg-bridge-success/10 text-bridge-success",
    warning: "border-bridge-warning/30 bg-bridge-warning/10 text-bridge-warning",
    danger: "border-bridge-danger/30 bg-bridge-danger/10 text-bridge-danger",
  }[active ? tone : "neutral"];

  return (
    <Tooltip content={hint} side="top">
      <button
        type="button"
        aria-pressed={selected}
        // Left enabled even when there is nothing to filter to: a disabled
        // button emits no mouse events, and the tooltip is the tile's
        // explanation of what its number counts.
        onClick={active ? onClick : undefined}
        className={cn(
          "rounded-lg border px-3 py-2.5 text-center transition-all",
          toneClass,
          onClick && active ? "cursor-pointer hover:shadow-soft" : "cursor-default",
          selected && "ring-2 ring-offset-1 ring-current",
        )}
      >
        <div className={cn("text-xl font-bold tabular-nums leading-tight", !active && "text-bridge-subtle")}>
          {value}
        </div>
        <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      </button>
    </Tooltip>
  );
}

/** How far a run slipped past the moment it was due, when that is worth saying. */
function lateBy(run: CampaignRun): string | null {
  if (!run.scheduled_for || !run.started_at) return null;
  const drift = new Date(run.started_at).getTime() - new Date(run.scheduled_for).getTime();
  // Sub-minute drift is the scheduler doing its job, not something to report.
  return drift >= 60_000 ? `${formatDuration(drift)} late` : null;
}

/**
 * One dispatch, laid out across the pane rather than hidden behind a hover:
 * when it started and finished, how it ended, and what it actually delivered.
 * Only the failure reason still needs a tooltip — everything else fits.
 */
function RunRow({ run, timeZone }: { run: CampaignRun; timeZone: string | undefined }) {
  const status = displayRunStatus(run);
  const started = zonedParts(run.started_at, timeZone);
  const ended = zonedParts(run.ended_at, timeZone);
  const scheduled = zonedParts(run.scheduled_for, timeZone);
  // A run that never started still has the time it was meant to — showing that
  // is what keeps a queued or failed-to-start run on the timeline.
  const shown = started ?? scheduled;
  const duration = formatDuration(run.duration_ms);
  const late = lateBy(run);
  const showEnd = (run.duration_ms ?? 0) >= 60_000;

  const targeted = run.targeted_count ?? 0;
  const sent = run.sent_count ?? 0;
  const failed = run.failed_count ?? 0;
  const skipped = run.skipped_count ?? 0;
  const pct = (n: number) => (targeted > 0 ? `${Math.min(100, (n / targeted) * 100)}%` : "0%");
  /** How much of the run got through — the same sent/targeted share the delivery card reports. */
  const completedPct = targeted > 0 ? Math.min(100, Math.round((sent / targeted) * 100)) : 0;

  return (
    <li className="flex items-center gap-4 rounded-lg px-2 py-3 transition-colors hover:bg-bridge-bg/60">
      <div className="w-[140px] shrink-0">
        <div className="text-sm font-semibold tabular-nums text-bridge-fg">
          {shown?.seconds ?? "—"}
          {/* The end time is only worth a column when the run was long enough
              for it to differ meaningfully from the start — for the usual
              two-second dispatch the duration below already says it, and
              "15:51:50 → 15:51:51" is noise. Same precision either way. */}
          {ended && showEnd && <span className="font-normal text-bridge-subtle"> → {ended.seconds}</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] tabular-nums text-bridge-subtle">
          {duration && <span>{duration}</span>}
          {run.trigger_type && <span className="lowercase">· {String(run.trigger_type).toLowerCase()}</span>}
          {late && <span className="text-bridge-warning">· {late}</span>}
        </div>
      </div>

      <Badge tone={RUN_STATUS_TONE[status] ?? "neutral"} className="w-[104px] shrink-0 justify-center">
        {IN_FLIGHT.has(status) && <Loader2 size={11} className="animate-spin" />}
        {status}
      </Badge>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3 text-xs tabular-nums">
          <span className="text-bridge-fg">
            <span className="font-semibold">{sent}</span>
            <span className="text-bridge-subtle">/{targeted}</span> sent
          </span>
          <span className="flex items-center gap-2.5">
            {failed > 0 && <span className="font-semibold text-bridge-danger">{failed} failed</span>}
            {skipped > 0 && <span className="text-bridge-warning">{skipped} skipped</span>}
            <span className="text-bridge-subtle">{completedPct}%</span>
          </span>
        </div>
        {/* One bar per outcome, laid end to end — the shape of the run at a
            glance, where three separate numbers would have to be compared. */}
        <div className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-bridge-border/50">
          <div className="bg-bridge-success transition-[width]" style={{ width: pct(sent) }} />
          <div className="bg-bridge-danger transition-[width]" style={{ width: pct(failed) }} />
          <div className="bg-bridge-warning transition-[width]" style={{ width: pct(skipped) }} />
        </div>
      </div>

      {run.error_summary ? (
        // The reason a run failed is the whole point of noticing it did — but
        // it is a stack-trace-ish sentence, so it stays on an icon. `side` is
        // only a hint: the bubble flips itself when the drawer sits too close
        // to the edge of the screen to open leftwards.
        <Tooltip content={run.error_summary} side="left">
          <span className="shrink-0 text-bridge-danger">
            <AlertTriangle size={16} />
          </span>
        </Tooltip>
      ) : (
        // Keeps the delivery bars of every row ending on the same line.
        <span className="w-4 shrink-0" />
      )}
    </li>
  );
}

/**
 * A campaign's delivery history: how many times it has run, how many of those
 * runs failed, when the next one is due, and every past run on a day-by-day
 * timeline.
 *
 * The campaign's own `delivery_summary` only ever describes the newest run, so
 * a recurring campaign that has been failing all week looks fine there — the
 * counts and the timeline below are what make that visible without opening
 * anything else.
 */
export function CampaignRunsSection({
  campaign,
  state,
}: {
  campaign: Campaign;
  state: CampaignRunsState;
}) {
  const timeZone = resolvableZone(campaign.timezone);
  const [filter, setFilter] = useState<Outcome | null>(null);
  const { runs, tally, loading, error } = state;

  /** Runs the list shows, newest first, grouped under the day they ran on. */
  const groups = useMemo(() => {
    const visible = filter
      ? runs.filter((run) => displayRunStatus(run) === filter)
      : runs;
    const today = zonedParts(new Date().toISOString(), timeZone)?.day;
    const byDay = new Map<string, CampaignRun[]>();
    for (const run of visible) {
      const day = zonedParts(run.started_at ?? run.scheduled_for, timeZone)?.day ?? "Undated";
      const bucket = byDay.get(day);
      if (bucket) bucket.push(run);
      else byDay.set(day, [run]);
    }
    return [...byDay].map(([day, dayRuns]) => ({
      day,
      label: day === today ? `Today · ${day}` : day,
      runs: dayRuns,
    }));
  }, [runs, filter, timeZone]);

  const nextRun = campaign.next_run_at;
  const nextRunParts = zonedParts(nextRun, timeZone);
  const nextRunUpcoming = nextRun ? new Date(nextRun).getTime() > Date.now() : false;
  const inactive = String(campaign.status ?? "").toUpperCase() === "INACTIVE";

  return (
    <section className="bg-white border border-bridge-border rounded-xl p-5 shadow-card transition-all duration-200 hover:shadow-soft">
      <div className="flex items-center gap-2 border-b border-bridge-border/80 pb-3 mb-4">
        <History size={16} className="text-bridge-accent" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-bridge-fg">Run History</h3>
        <span className="ml-auto flex items-center gap-2">
          {tally.running > 0 && (
            <Badge tone="info">
              <Loader2 size={11} className="animate-spin" />
              {tally.running} in progress
            </Badge>
          )}
          {timeZone && (
            <Tooltip content="Every time below is shown in the campaign's own timezone." side="left">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-bridge-subtle">
                {timeZone}
              </span>
            </Tooltip>
          )}
        </span>
      </div>

      {/* The next fire is the one timing the history cannot show, and the one
          a scheduled campaign is usually opened to check. */}
      {inactive ? (
        <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-bridge-border/70 bg-slate-50 px-3 py-2.5">
          <CalendarClock size={15} className="shrink-0 text-bridge-muted" />
          <span className="text-xs font-semibold text-bridge-fg">Next run</span>
          <span className="ml-auto text-xs font-semibold text-bridge-muted">Inactive</span>
        </div>
      ) : nextRunParts && nextRunUpcoming ? (
        <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-bridge-accent/30 bg-bridge-accent/5 px-3 py-2.5">
          <CalendarClock size={15} className="shrink-0 text-bridge-accent" />
          <span className="text-xs font-semibold text-bridge-fg">Next run</span>
          <span className="ml-auto text-right text-xs tabular-nums text-bridge-muted">
            {nextRunParts.day}, {nextRunParts.time}
            <span className="ml-2 text-bridge-accent">{relativeTo(nextRun)}</span>
          </span>
        </div>
      ) : null}

      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-bridge-subtle">Couldn&apos;t load this campaign&apos;s run history.</p>
      )}

      {!loading && !error && tally.total === 0 && (
        <p className="text-sm text-bridge-subtle/40">
          {inactive
            ? "This campaign is inactive, so it will not run again until it is turned back on."
            : nextRunUpcoming
              ? "No runs yet — the first is scheduled above."
              : "This campaign has not run yet."}
        </p>
      )}

      {!loading && !error && tally.total > 0 && (
        <>
          <div className="grid grid-cols-4 gap-2">
            <RunStat
              label="Runs"
              value={tally.total}
              hint={filter ? "Show every run again." : "Times this campaign has been dispatched."}
              selected={filter === null}
              onClick={() => setFilter(null)}
            />
            <RunStat
              label="Completed"
              value={tally.completed}
              tone="success"
              hint="Runs that delivered to every targeted contact. Click to list only these."
              selected={filter === "COMPLETED"}
              onClick={() => setFilter(filter === "COMPLETED" ? null : "COMPLETED")}
            />
            <RunStat
              label="Partial"
              value={tally.partial}
              tone="warning"
              hint="Runs that reached some contacts but not all. Click to list only these."
              selected={filter === "PARTIAL"}
              onClick={() => setFilter(filter === "PARTIAL" ? null : "PARTIAL")}
            />
            <RunStat
              label="Failed"
              value={tally.failed}
              tone="danger"
              hint={
                tally.failedRecipients > 0
                  ? `Runs that delivered nothing — ${tally.failedRecipients} recipient${tally.failedRecipients === 1 ? "" : "s"} failed across all runs. Click to list only these.`
                  : "Runs that delivered nothing. Click to list only these."
              }
              selected={filter === "FAILED"}
              onClick={() => setFilter(filter === "FAILED" ? null : "FAILED")}
            />
          </div>

          {/* Every run is listed rather than a recent few — a campaign that has
              fired hourly for a week has a history worth scrolling, and the day
              headings keep it readable while doing so. */}
          <div className="scrollbar-metal mt-4 max-h-[46vh] overflow-y-auto pr-1">
            {groups.length === 0 ? (
              <p className="py-3 text-center text-xs text-bridge-subtle">
                No {filter?.toLowerCase()} runs.
              </p>
            ) : (
              groups.map((group) => (
                <div key={group.day}>
                  <div className="sticky top-0 z-10 flex items-center gap-3 bg-white/95 py-2 backdrop-blur-sm">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-bridge-subtle">
                      {group.label}
                    </span>
                    <span className="h-px flex-1 bg-bridge-border/70" />
                    <span className="text-[10px] font-semibold tabular-nums text-bridge-subtle">
                      {group.runs.length} run{group.runs.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ul className="divide-y divide-bridge-border/50">
                    {group.runs.map((run) => (
                      <RunRow key={run.run_id} run={run} timeZone={timeZone} />
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>

          {tally.total > runs.length && (
            <p className="mt-2 text-[11px] text-bridge-subtle">
              Showing the most recent {runs.length} of {tally.total} runs.
            </p>
          )}
        </>
      )}
    </section>
  );
}
